import React from 'react';
import { Types } from '@fiverr-private/theme';
import { GetBriefResponseDTOError } from './services';
import { BriefContextValues } from './common/BriefContext';

import {
    BriefSectionContentProps,
    BriefSectionViewContentProps,
} from './components/EditBrief/IncludedSections/BriefSection/SectionTypes';
import type { CustomSectionTitleComponentProps } from './components/common/SectionTitle';

export enum SectionTypes {
    shortText = 'shortText',
    attachments = 'attachments',
    dynamic = 'dynamic',
    view = 'view',
    metadata = 'metadata',
    generated = 'generated',
}

export type SectionInputTypes = 'textarea' | 'input';
export type BriefSectionCtaType = 'save' | 'cancel' | 'remove' | undefined;

export interface BriefSection<TContent = string> {
    id: string;
    focused?: boolean;
    TitleComponent?: React.FC<CustomSectionTitleComponentProps>;
    title?: string;
    content: TContent;
    type?: SectionTypes;
    attachments?: ConnectedAttachmentData[] | FileAttachmentData[] | ApiTransformedAttachment[];
    included: boolean;
    readOnly?: boolean;
    disableViewInteraction?: boolean;
    group?: string;
    inline?: boolean;
    mandatory?: boolean;
    hideActions?: boolean;
    onEditStart?: (content: BriefSection<TContent>) => void;
    onEditEnd?: (section: BriefSection<TContent>, ctaType: BriefSectionCtaType) => void;
    onAdd?: (section: BriefSection<TContent>) => void;
    onHide?: (section: BriefSection<TContent>) => void;
    showWhen?: (editedBrief: Brief) => boolean;
    customValidator?: (section: BriefSection<TContent>) => boolean;
    disableInitialScroll?: boolean;
    moveActionsToSide?: (section: BriefSection<TContent>) => boolean;
    extraHighlighted?: boolean;
    placeholder?: string;
    customMargin?: (
        section: BriefSection<TContent>,
        editedSectionId: string | null
    ) => Partial<
        Record<'margin' | 'marginRight' | 'marginLeft' | 'marginTop' | 'marginBottom', Types.ResponsiveSpacingType>
    >;
}

export interface DynamicSection<TContent = any> extends BriefSection<TContent> {
    ViewComponent?: React.FC<BriefSectionViewContentProps<TContent>>;
    EditComponent: React.FC<BriefSectionContentProps<TContent>>;
    content: TContent;
    mandatoryType?: string;
}

export interface ViewSection<TContent = any> extends BriefSection<TContent> {
    content: TContent;
    ViewComponent: React.FC<BriefSectionViewContentProps<TContent>>;
    editSectionId: string;
}

export interface Brief<TSectionContent = string> {
    id?: string;
    title: string;
    deletedAttachmentIds?: string[];
    sections: BriefSection<TSectionContent>[];
    intentClassification?: string;
    error?: GetBriefResponseDTOError;
    description?: string;
}

export interface BriefItem {
    id: string;
    title: string;
    userDisplayName: string;
    createDate: Date;
}

export interface Seller {
    id: number;
    userName: string;
    displayName: string;
    responseTime: number;
}

export interface BusinessInfo {
    companyName: string;
    companyDescription: string;
    organizationType: string;
    businessStage: string;
    numberOfEmployees: string;
    industryTypes: string[];
    companyWebsite: string;
    companyLogo: string;
    industryIds: string[];
    businessType: string | null;
}

export type StringifiedNumber = string;

export interface FileAttachmentData {
    id: string;
    attachment_id?: string;
    provider_id?: string;
    content_type?: string;
    created_at?: string;
    type: string;
    file_size?: number;
    fileName: string;
    preview_type?: string;
    file_name?: string;
    sortId: StringifiedNumber;
    error?: string;
    progress?: number;
    fileSize: string;
    size: number;
    extension: string;
    name?: string;
    loaded?: boolean;
    attachment_name?: string;
    preview_state?: string;
    coverImageUrl?: string;
    preview_urls?: {
        original?: string;
        large?: string;
        thumb_large?: string;
        mobile_preview?: string;
        mobile_static_preview?: string;
    };
    watermarked_preview_urls?: {
        original: string;
        large: string;
        thumb_large: string;
        mobile_preview: string;
        mobile_static_preview: string;
    };
    open_url?: string | null;
    video_urls?: { [key: string]: string };
    download_url?: string;
    watermarked_download_url?: string;
    antivirus_result?: string | null;
    pending_attachment_id?: string;
}

export type ConnectedAttachmentData = FileAttachmentData;

export interface ApiTransformedAttachment {
    id: string;
    pending_attachment_id?: string;
    type: string;
    fileName: string;
    name: string;
    extension: string;
    loaded: boolean;
    sortId: StringifiedNumber;
    fileSize: string;
    size: number;
    download_url?: string;
    open_url?: string | null;
    coverImageUrl?: string;
    preview_urls?: {
        original?: string;
        large?: string;
        thumb_large?: string;
    };
}

export interface BriefDrawerProps {
    isOpen: boolean;
    shouldRefreshData: boolean;
    onClose: () => void;
    subCategoryId: BriefContextValues['subCategoryId'];
    categoryId: BriefContextValues['categoryId'];
    onLoadingChange?: (isLoading: boolean) => void;
    seller: Seller;
    source: string;
    componentName: string;
    onContactSellerClick?: () => void;
    initialText?: string;
}
